import React, { useState, useEffect } from "react";
import api from '../../lib/axios';
import { toast } from "sonner";
import {
  FolderOpen,
  Copy,
  Trash2,
  Download,
  ChevronRight,
  Search,
  Plus,
  FileText,
  Package,
  DollarSign,
  Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'USD'
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('es-PE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const ProjectsManager = ({ 
  isOpen, 
  onClose, 
  onLoadProject,
  onDuplicateProject 
}) => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [projectToDelete, setProjectToDelete] = useState(null);

  // Fetch projects on open
  useEffect(() => {
    if (isOpen) {
      fetchProjects();
    }
  }, [isOpen]);

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`${API}/projects`);
      setProjects(response.data || []);
    } catch (error) {
      // console.error("Error fetching projects:", error);
      toast.error("Error al cargar proyectos");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadProject = async (project) => {
    try {
      // Fetch project details with items
      const response = await api.get(`${API}/projects/${encodeURIComponent(project.name)}/items`);
      
      if (onLoadProject) {
        onLoadProject({
          project: response.data.project || project,
          items: response.data.items || []
        });
      }
      
      toast.success(`Proyecto "${project.name}" cargado`);
      onClose();
    } catch (error) {
      // console.error("Error loading project:", error);
      toast.error("Error al cargar el proyecto");
    }
  };

  const handleDuplicateProject = async () => {
    if (!selectedProject) return;
    
    try {
      const response = await api.post(
        `${API}/projects/${encodeURIComponent(selectedProject.name)}/duplicate`,
        null,
        { params: { new_name: newProjectName || null } }
      );
      
      toast.success(`Proyecto duplicado: "${response.data.new_project_name}"`);
      setDuplicateDialogOpen(false);
      setSelectedProject(null);
      setNewProjectName("");
      fetchProjects();
      
      if (onDuplicateProject) {
        onDuplicateProject(response.data.new_project_name);
      }
    } catch (error) {
      // console.error("Error duplicating project:", error);
      toast.error(error.response?.data?.detail || "Error al duplicar proyecto");
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    
    try {
      await api.delete(`${API}/projects/${encodeURIComponent(projectToDelete.name)}`);
      toast.success(`Proyecto "${projectToDelete.name}" eliminado`);
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
      fetchProjects();
    } catch (error) {
      // console.error("Error deleting project:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar proyecto");
    }
  };

  const openDuplicateDialog = (project) => {
    setSelectedProject(project);
    setNewProjectName(`${project.name} (Copia)`);
    setDuplicateDialogOpen(true);
  };

  const openDeleteDialog = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  // Filter projects based on search
  const filteredProjects = projects.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-blue-600" />
              Gestión de Proyectos
            </DialogTitle>
            <DialogDescription>
              Carga, duplica o elimina proyectos existentes con sus datos de instalación y PDR
            </DialogDescription>
          </DialogHeader>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar proyecto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Projects List */}
          <div className="flex-1 overflow-y-auto space-y-3 py-2">
            {isLoading ? (
              <div className="text-center py-8 text-slate-500">Cargando...</div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                {searchTerm ? "No se encontraron proyectos" : "No hay proyectos guardados"}
              </div>
            ) : (
              filteredProjects.map((project) => (
                <Card key={project.id || project.name} className="hover:bg-slate-50 transition">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-slate-900">{project.name}</h3>
                          {project.total_supply > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {formatCurrency(project.total_supply)}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          {/* Installation */}
                          {(project.installation_uti_percent || project.installation_cos_m2i) && (
                            <div className="flex items-center gap-1 text-green-600">
                              <Package className="w-4 h-4" />
                              <span>
                                UTI: {project.installation_uti_percent || 0}% | 
                                M2i: ${project.installation_cos_m2i || 0}
                              </span>
                            </div>
                          )}
                          
                          {/* PDR */}
                          {(project.pdr_quantity || project.pdr_unit_value) && (
                            <div className="flex items-center gap-1 text-orange-600">
                              <FileText className="w-4 h-4" />
                              <span>
                                PDR: {project.pdr_quantity || 0} x ${project.pdr_unit_value || 0}
                              </span>
                            </div>
                          )}
                          
                          {/* Date */}
                          <div className="flex items-center gap-1 text-slate-500">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(project.updated_at || project.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleLoadProject(project)}
                          className="gap-1"
                          title="Cargar proyecto"
                        >
                          <Download className="w-4 h-4" />
                          Cargar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDuplicateDialog(project)}
                          className="text-blue-600"
                          title="Duplicar proyecto"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(project)}
                          className="text-red-500"
                          title="Eliminar proyecto"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-blue-600" />
              Duplicar Proyecto
            </DialogTitle>
            <DialogDescription>
              Se creará una copia de "{selectedProject?.name}" con todos sus elementos
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="new-name">Nombre del nuevo proyecto</Label>
            <Input
              id="new-name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder="Nombre del proyecto"
              className="mt-2"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleDuplicateProject} className="gap-2">
              <Copy className="w-4 h-4" />
              Duplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar proyecto?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará permanentemente el proyecto "{projectToDelete?.name}" y todos sus elementos asociados. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ProjectsManager;
